import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/**
 * A small set of inline SVG icons in the brand's stroke style. Centralised so
 * components don't import an icon font and we keep the bundle lean.
 */

const baseProps: IconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

/**
 * Base props for the brand product glyphs (Lens, Curate). These use the
 * official artwork from the Kai icon library (Flat variant) filled with
 * `currentColor` so they inherit nav active/hover and dark-mode colors just like
 * the stroke icons above. Each glyph sets its own tightly-cropped `viewBox`
 * (below) so the artwork fills roughly the same fraction of the box as the
 * stroke icons and the row stays visually balanced.
 */
const glyphBaseProps: IconProps = {
  fill: 'currentColor',
  'aria-hidden': true,
};

export const SunIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

export const MoonIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </svg>
);

export const BellIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

export const SettingsIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 4.96l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

export const LogoutIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export const UserIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const ChevronDownIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const MicrosoftIcon = (props: IconProps) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

export const ShieldCheckIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

export const HomeIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M3 11.5 12 4l9 7.5V20a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2Z" />
  </svg>
);

export const LensIcon = (props: IconProps) => (
  <svg viewBox="11 11.4 75 75" {...glyphBaseProps} {...props}>
    <path d="M29 74.75H68C70.485 74.75 72.5 72.735 72.5 70.25H24.5C24.5 72.735 26.515 74.75 29 74.75ZM71 23H26C21.8575 23 18.5 26.3575 18.5 30.5V58.25C18.5 62.3925 21.8575 65.75 26 65.75H71C75.1425 65.75 78.5 62.3925 78.5 58.25V30.5C78.5 26.3575 75.1425 23 71 23ZM36.5 56.25H29V40.5H36.5V56.25ZM47 56.25H39.5V31.5H47V56.25ZM57.5 56.25H50V36.5H57.5V56.25ZM68 56.25H60.5V45H68V56.25Z" />
  </svg>
);

export const CurateIcon = (props: IconProps) => (
  <svg viewBox="11.4 9.6 73.1 73.1" {...glyphBaseProps} {...props}>
    <path d="M66.75 25.75H29.25C26.765 25.75 24.75 27.765 24.75 30.25H71.25C71.25 27.765 69.235 25.75 66.75 25.75ZM61.25 18H34.75C32.5412 18 30.75 19.7912 30.75 22H65.25C65.25 19.7912 63.4588 18 61.25 18ZM69.75 34.25H26.25C22.1075 34.25 18.75 37.6075 18.75 41.75V66.75C18.75 70.8925 22.1075 74.25 26.25 74.25H69.75C73.8925 74.25 77.25 70.8925 77.25 66.75V41.75C77.25 37.6075 73.8925 34.25 69.75 34.25ZM71.25 48.25C68.25 48.25 64.25 52.25 64.25 55.25H63.25C63.25 52.25 59.25 48.25 56.25 48.25V47.25C59.25 47.25 63.25 43.25 63.25 40.25H64.25C64.25 43.25 68.25 47.25 71.25 47.25V48.25Z" />
  </svg>
);

export const ArrowRightIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export const SparkleIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </svg>
);

/**
 * Bare "i" glyph, no enclosing circle. Every info trigger in the app renders
 * this inside its own round button, so a circled glyph read as a bubble
 * inside a bubble. Heavier stroke than the base set so it stays legible at
 * the 12-14px sizes it is used at.
 */
export const InfoIcon = (props: IconProps) => (
  <svg {...baseProps} strokeWidth={2.75} {...props}>
    <line x1="12" y1="11" x2="12" y2="18" />
    <circle cx="12" cy="6.25" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

export const TrendUpIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="15 7 21 7 21 13" />
  </svg>
);

export const TrendDownIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <polyline points="3 7 9 13 13 9 21 17" />
    <polyline points="15 17 21 17 21 11" />
  </svg>
);

/** Question mark in a circle (help / support). */
export const HelpIcon = (props: IconProps) => (
  <svg {...baseProps} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.4a2.5 2.5 0 1 1 3.6 2.3c-.8.4-1.2 1-1.2 1.8v.3" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);
